// Visit-scoped Przypomnienia tab rendered inside both the org-side
// `OrganizationVisitModalDetailsTabs` and the user-facing
// `VisitModalDetailsTabs`. Visually mirrors `ProfileRemindersContent`
// — same table, same form modal, same delete modal — but pulls the
// list from `reminderApi.reminderControllerGetVisitReminders(visitId)`
// so guest-visit reminders (no profileId) also surface.
//
// Editability rules are owned by the BE: org staff get `canEdit` per
// reminder via `GetReminderDto.canEdit`, the auth visit owner can
// always edit, and guests are time-boxed to 30 days post-completion
// inside `ReminderService.updateReminderByGuest`. The form modal honors
// `canEdit`, so there's nothing to gate at this layer.
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { organizationProfileApi } from 'src/api/organization-profile';
import { reminderApi } from 'src/api/reminder';
import ProfileRemindersDeleteModal from 'src/sections/@dashboard/profile/components/ProfileRemindersDeleteModal';
import ProfileRemindersFormModal, {
  RemindersFormSubmitValues,
} from 'src/sections/@dashboard/profile/components/ProfileRemindersFormModal';
import { formatRecurrence } from 'src/sections/@dashboard/profile/components/ProfileRemindersTable';
import ProfileRemindersTableSkeleton from 'src/sections/@dashboard/profile/components/ProfileRemindersTableSkeleton';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { translateKey } from 'src/locales/translate';
import {
  CreateReminderDto,
  GetReminderDtoNotificationChannelsEnum,
  UpdateReminderDto,
} from 'woofwoof-api';

type VisitModalRemindersTabProps = {
  visitId: string;
  mutationScope?: 'owner' | 'organization';
  // Auth visits carry a profile; guest visits do not. The form modal's
  // create flow needs profileId on `CreateReminderDto` — we just hide
  // the "Add" button when null (staff can still add via the live-visit
  // page; guests can't add at all by policy).
  profileId?: string | null;
};

export default function VisitModalRemindersTab({
  visitId,
  profileId,
  mutationScope = 'owner',
}: VisitModalRemindersTabProps) {
  const { selectedOrganization } = useOrganizationContext();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reminderId, setReminderId] = useState('');

  const { data, isLoading } = useQuery(
    ['visit-reminders', visitId],
    () => reminderApi.reminderControllerGetVisitReminders({ visitId }),
    { retry: 1, enabled: Boolean(visitId) },
  );

  const invalidate = () => {
    queryClient.invalidateQueries(['visit-reminders', visitId]);
    queryClient.invalidateQueries(['visit-activity', visitId]);
    if (profileId) {
      queryClient.invalidateQueries(['profile-reminders', profileId]);
    }
  };

  const { mutate: createReminder, isLoading: isCreating } = useMutation({
    mutationFn: (payload: CreateReminderDto) =>
      mutationScope === 'organization'
        ? organizationProfileApi.organizationProfileControllerAddVisitReminder(
            {
              visitId,
              createReminderDto: payload,
            },
          )
        : reminderApi.reminderControllerCreateReminder({
            createReminderDto: payload,
          }),
    onSuccess: () => {
      invalidate();
      enqueueSnackbar(
        translateKey('hardcoded.przypomnienie_zostalo_dodane'),
        { variant: 'success' },
      );
      setIsModalOpen(false);
    },
    onError: () => {
      enqueueSnackbar(
        translateKey('hardcoded.wystapil_blad_podczas_dodawania_przypomnienia'),
        { variant: 'error' },
      );
    },
  });

  const { mutate: updateReminder, isLoading: isUpdating } = useMutation({
    mutationFn: (payload: UpdateReminderDto) =>
      mutationScope === 'organization'
        ? organizationProfileApi.organizationProfileControllerUpdateVisitReminder(
            {
              visitId,
              reminderId,
              updateReminderDto: { ...payload, visitId },
            },
          )
        : reminderApi.reminderControllerUpdateReminder({
            reminderId,
            updateReminderDto: { ...payload, visitId },
          }),
    onSuccess: () => {
      invalidate();
      enqueueSnackbar(
        translateKey('hardcoded.przypomnienie_zostalo_zaktualizowane'),
        { variant: 'success' },
      );
      setIsModalOpen(false);
      setTimeout(() => setReminderId(''), 500);
    },
    onError: () => {
      enqueueSnackbar(
        translateKey('hardcoded.wystapil_blad_podczas_aktualizacji_przypomnienia'),
        { variant: 'error' },
      );
    },
  });

  const { mutate: deleteReminder, isLoading: isDeleting } = useMutation({
    mutationFn: () =>
      mutationScope === 'organization'
        ? organizationProfileApi.organizationProfileControllerRemoveVisitReminder(
            {
              reminderId,
              visitId,
            },
          )
        : reminderApi.reminderControllerDeleteReminder({
            reminderId,
            visitId,
          }),
    onSuccess: () => {
      invalidate();
      enqueueSnackbar(
        translateKey('hardcoded.przypomnienie_zostalo_usuniete'),
        { variant: 'success' },
      );
      setIsModalOpen(false);
      setIsDeleteModalOpen(false);
      setTimeout(() => setReminderId(''), 500);
    },
    onError: () => {
      enqueueSnackbar(
        translateKey('hardcoded.wystapil_blad_podczas_usuwania_przypomnienia'),
        { variant: 'error' },
      );
    },
  });

  const handleOnRowClick = (id: string) => {
    setReminderId(id);
    setIsModalOpen(true);
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setReminderId('');
  };

  const selectedReminder = reminderId
    ? data?.data.find((r) => r.id === reminderId)
    : undefined;

  const canEditSelectedReminder = selectedReminder?.canEdit ?? true;

  const handleOnSubmit = useCallback(
    (values: RemindersFormSubmitValues) => {
      if (reminderId && !canEditSelectedReminder) return;
      if (reminderId) {
        updateReminder(values as UpdateReminderDto);
      } else if (profileId) {
        createReminder({
          ...values,
          profileId,
          ...(selectedOrganization?.id
            ? { organizationId: selectedOrganization.id }
            : {}),
          visitId,
        } as CreateReminderDto);
      }
    },
    [
      canEditSelectedReminder,
      createReminder,
      profileId,
      reminderId,
      selectedOrganization?.id,
      updateReminder,
      visitId,
    ],
  );

  if (isLoading) {
    return <ProfileRemindersTableSkeleton />;
  }

  const reminders = data?.data ?? [];

  if (reminders.length === 0) {
    return (
      <Stack sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Brak przypomnień przypisanych do tej wizyty
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {translateKey('search.ui.aboutTab')}
              </TableCell>
              <TableCell>{translateKey('visit.tableDate')}</TableCell>
              <TableCell>Powtarzanie</TableCell>
              <TableCell>{translateKey('visit.tableKind')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reminders.map((reminder) => (
              <TableRow
                key={reminder.id}
                hover={reminder.canEdit}
                onClick={
                  reminder.canEdit
                    ? () => handleOnRowClick(reminder.id)
                    : undefined
                }
                sx={reminder.canEdit ? { cursor: 'pointer' } : undefined}
              >
                <TableCell>
                  <Tooltip title={reminder.description} arrow>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        maxWidth: 240,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {reminder.description}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {format(
                      new Date(reminder.scheduledAt),
                      'dd/MM/yyyy HH:mm',
                      { locale: pl },
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatRecurrence(
                      reminder.recurrenceType,
                      reminder.customRecurrence,
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {reminder.notificationChannels
                      .map((ch) =>
                        ch ===
                        GetReminderDtoNotificationChannelsEnum.Email
                          ? translateKey('search.book.cardEmail')
                          : 'SMS',
                      )
                      .join(', ')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ProfileRemindersFormModal
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={() => setIsDeleteModalOpen(true)}
        reminder={selectedReminder}
        canEdit={canEditSelectedReminder}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        singleReminderOnly={!profileId}
      />

      <ProfileRemindersDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        onDelete={deleteReminder}
        isLoading={isDeleting}
      />
    </>
  );
}
